import { makeObservable, observable, action, runInAction } from 'mobx';
import { getProcessLibraryById, updateProcessLibrary, getWorkflowCatalog, getEffectiveWorkflow } from '@/api/processLibrary';
import type { AIProcessLibrary, WorkflowCatalogEntry } from '@/api/types';
import type { RootStore } from './RootStore';

// Holds workflow library list + selected detail. Auto-fetches on merchant
// change via reaction in RootStore (mirrors AgentRegistryStore lifecycle).

export class ProcessLibraryStore {
  /** Every workflow as it runs for the selected merchant — rows AND code-only (GET Catalog). */
  @observable workflows: WorkflowCatalogEntry[] = [];
  /** The database row behind the open workflow, when one exists (editing goes through it). */
  @observable selected: AIProcessLibrary | null = null;
  @observable selectedId: number | null = null;
  /** The open workflow as it runs: effective steps, source, agents (GET Effective). */
  @observable effective: WorkflowCatalogEntry | null = null;
  @observable isLoadingEffective = false;
  @observable effectiveError: string | null = null;

  @observable isLoadingList = false;
  @observable isLoadingDetail = false;
  @observable isSaving = false;
  @observable listError: string | null = null;
  @observable detailError: string | null = null;
  @observable saveError: string | null = null;

  @observable searchTerm = '';

  private root: RootStore;

  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this);
  }

  @action
  setSearchTerm(term: string): void {
    this.searchTerm = term;
  }

  @action
  async loadList(): Promise<void> {
    this.isLoadingList = true;
    this.listError = null;
    try {
      const data = await getWorkflowCatalog(this.root.merchantContextStore.selectedMerchantId ?? null);
      runInAction(() => {
        this.workflows = data;
      });
    } catch (e) {
      runInAction(() => {
        this.listError = e instanceof Error ? e.message : String(e);
      });
    } finally {
      runInAction(() => {
        this.isLoadingList = false;
      });
    }
  }

  @action
  async loadDetail(id: number): Promise<void> {
    this.selectedId = id;
    this.selected = null;
    this.isLoadingDetail = true;
    this.detailError = null;
    try {
      const data = await getProcessLibraryById(id);
      runInAction(() => {
        this.selected = data;
      });
      if (data?.name) await this.loadEffective(data.name);
    } catch (e) {
      runInAction(() => {
        this.detailError = e instanceof Error ? e.message : String(e);
      });
    } finally {
      runInAction(() => {
        this.isLoadingDetail = false;
      });
    }
  }

  /** The workflow as it runs. Called after a row loads, or alone for a code-only workflow (no row). */
  @action
  async loadEffective(name: string): Promise<void> {
    this.isLoadingEffective = true;
    this.effectiveError = null;
    try {
      const data = await getEffectiveWorkflow(name, this.root.merchantContextStore.selectedMerchantId ?? null);
      runInAction(() => {
        this.effective = data;
      });
    } catch (e) {
      runInAction(() => {
        this.effectiveError = e instanceof Error ? e.message : String(e);
      });
    } finally {
      runInAction(() => {
        this.isLoadingEffective = false;
      });
    }
  }

  @action
  clearDetail(): void {
    this.selected = null;
    this.selectedId = null;
    this.effective = null;
    this.detailError = null;
    this.effectiveError = null;
    this.saveError = null;
  }

  // Optimistic save: swap in the proposed model immediately, fire the
  // request in the background, revert on error. The list slice gets the
  // saved row spliced in so the cards reflect the new state without a
  // re-fetch.
  @action
  async saveSelected(next: AIProcessLibrary): Promise<void> {
    const previous = this.selected;
    if (!previous || previous.id !== next.id) {
      throw new Error('No matching workflow loaded — cannot save');
    }

    runInAction(() => {
      this.selected = next;
      this.isSaving = true;
      this.saveError = null;
    });

    try {
      const saved = await updateProcessLibrary(next);
      runInAction(() => {
        this.selected = saved;
      });
      // What runs may have changed (steps added/removed): re-read the effective view and the list.
      if (saved?.name) await this.loadEffective(saved.name);
      void this.loadList();
    } catch (e) {
      runInAction(() => {
        this.selected = previous;
        this.saveError = e instanceof Error ? e.message : String(e);
      });
      throw e;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  // Computed-like helper for filtered display when the user types in the
  // page-local search input. Cheap — only called against ≤ a few dozen rows.
  filtered(): WorkflowCatalogEntry[] {
    const t = this.searchTerm.trim().toLowerCase();
    if (!t) return this.workflows;
    return this.workflows.filter(
      (w) =>
        (w.title ?? '').toLowerCase().includes(t) ||
        (w.name ?? '').toLowerCase().includes(t) ||
        w.agents.some((a) => (a.name ?? '').toLowerCase().includes(t))
    );
  }
}
